/**
 * Iter-236 — Campaign Intelligence Phase 2
 *
 * Event Insights Dashboard
 *   Route: /client/events/:eventKey/insights
 *   Route: /admin/events/:eventKey/insights (admin reuse)
 *
 * Auto-loads when a client opens a completed event. Shows the full
 * Executive KPI Strip, Campaign Performance tiles, AI Insights,
 * Team Performance, Benchmarks, AI Opportunities, and approved photos.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { toast } from 'sonner';
import {
  ArrowLeft, Sparkles, TrendingUp, TrendingDown, Users, Camera,
  MessageCircle, AlertTriangle, Award, Loader2, RefreshCw,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer,
  CartesianGrid,
} from 'recharts';
import CreatorAttributionPanel from '../../components/insights/CreatorAttributionPanel';

const fmt = (n) => (n || 0).toLocaleString();
const money = (cents) =>
  cents ? `$${(cents / 100).toLocaleString(undefined, { maximumFractionDigits: 0 })}` : '$0';

const Delta = ({ pct, label }) => {
  if (pct === null || pct === undefined) return null;
  const up = pct >= 0;
  return (
    <span
      className={`inline-flex items-center text-xs font-semibold ${
        up ? 'text-emerald-600' : 'text-rose-600'
      }`}
      data-testid={`delta-${label}`}
    >
      {up ? <TrendingUp className="w-3 h-3 mr-0.5" /> : <TrendingDown className="w-3 h-3 mr-0.5" />}
      {Math.abs(pct).toFixed(1)}%
    </span>
  );
};

// Iter-257 — KPI tile. Now accepts an optional `onClick` so the Executive
// Summary tiles in EventInsightsDashboard can deep-link to the relevant
// detail section. Without onClick the tile renders as a static card.
const KPI = ({ label, value, sub, testid, onClick }) => {
  const interactive = typeof onClick === 'function';
  return (
    <div
      className={
        'bg-white border border-slate-200 rounded-xl p-4 shadow-sm transition '
        + (interactive
          ? 'cursor-pointer hover:shadow-md hover:border-cyan-300 active:scale-[0.99]'
          : 'hover:shadow')
      }
      data-testid={testid}
      role={interactive ? 'button' : undefined}
      tabIndex={interactive ? 0 : undefined}
      onClick={onClick}
      onKeyDown={interactive ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } } : undefined}
    >
      <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">
        {label}
      </div>
      <div className="text-2xl md:text-3xl font-bold text-slate-900 mt-1">{value}</div>
      {sub != null && <div className="text-[11px] text-slate-500 mt-1">{sub}</div>}
    </div>
  );
};

const SectionHeader = ({ icon: Icon, title, subtitle, action }) => (
  <div className="flex items-start justify-between mb-3 mt-8">
    <div className="flex items-start gap-3">
      {Icon && <Icon className="w-5 h-5 text-cyan-500 mt-1" />}
      <div>
        <h2 className="text-lg font-bold text-slate-900">{title}</h2>
        {subtitle && <p className="text-xs text-slate-500 mt-0.5">{subtitle}</p>}
      </div>
    </div>
    {action}
  </div>
);

const ROLE_LABEL = {
  brand_ambassador: 'Brand Ambassador',
  sampling: 'Sampling Program',
  event_staff: 'Event Staff',
  team_lead: 'Team Lead',
  bartender: 'Bartender',
  server: 'Server',
  registration: 'Registration',
  street_team: 'Street Team',
  product_specialist: 'Product Specialist',
  field_marketing_manager: 'Field Marketing Manager',
  content_creator: 'Content Creator',
  influencer: 'Influencer',
};

export default function EventInsightsDashboard() {
  const { eventKey: rawKey } = useParams();
  const navigate = useNavigate();
  const eventKey = decodeURIComponent(rawKey || '');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [regenLoading, setRegenLoading] = useState(false);
  // Iter-262e — if the initial fetch lasts more than 5s, we're almost
  // certainly inside a lazy AI generation (Claude rollup takes ~20-30s).
  // Flip this so the spinner labels itself accurately.
  const [aiGenerating, setAiGenerating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setAiGenerating(false);
    const aiTimer = setTimeout(() => setAiGenerating(true), 5000);
    try {
      const res = await api.get(`/insights/event/${encodeURIComponent(eventKey)}`);
      setData(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load insights');
      setData(null);
    } finally {
      clearTimeout(aiTimer);
      setLoading(false);
      setAiGenerating(false);
    }
  }, [eventKey]);

  useEffect(() => { load(); }, [load]);

  const regenerate = useCallback(async () => {
    setRegenLoading(true);
    try {
      await api.post(`/insights/event/${encodeURIComponent(eventKey)}/regenerate-ai`);
      toast.success('AI insights refreshed');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'AI regeneration failed');
    } finally {
      setRegenLoading(false);
    }
  }, [eventKey, load]);

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-slate-50 px-6 text-center" data-testid="insights-loading">
        <div className="relative">
          <Loader2 className="w-10 h-10 animate-spin text-cyan-500" />
          {aiGenerating && (
            <Sparkles className="w-5 h-5 text-violet-500 absolute -top-1 -right-1 animate-pulse" />
          )}
        </div>
        <div className="mt-5 text-base font-semibold text-slate-900">
          {aiGenerating ? 'Generating AI Insights…' : 'Loading insights…'}
        </div>
        <div className="mt-1.5 text-xs text-slate-500 max-w-sm">
          {aiGenerating
            ? 'Our AI is analysing recap data, scoring sentiment, and writing a summary. This usually takes 20–30 seconds the first time you open this dashboard.'
            : 'Just a moment.'}
        </div>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-8 text-center">
        <h2 className="text-xl font-bold">Insights unavailable</h2>
        <Button onClick={() => navigate(-1)} className="mt-4">Back</Button>
      </div>
    );
  }

  const exec = data.executive_kpi || {};
  const perf = data.campaign_performance || {};
  const bench = data.benchmarks || {};
  const ai = data.ai_insights || {};
  const team = data.team_performance || {};

  return (
    <div className="min-h-screen bg-slate-50 pb-20" data-testid="event-insights-dashboard">
      {/* Header */}
      <div className="bg-gradient-to-br from-slate-900 to-slate-800 text-white">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <button
            onClick={() => navigate(-1)}
            className="text-cyan-300 text-sm flex items-center mb-3 hover:opacity-80"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </button>
          <div className="flex flex-wrap items-end justify-between gap-3">
            <div>
              <div className="text-xs uppercase tracking-widest text-cyan-300/70">
                Campaign Intelligence
              </div>
              <h1 className="text-2xl md:text-4xl font-bold mt-1" data-testid="event-label">
                {data.label}
              </h1>
              <div className="text-sm text-slate-300 mt-1">
                {data.location?.city}, {data.location?.state} · {data.date_range?.start} – {data.date_range?.end}
                {data.role_categories?.length > 0 && (
                  <> · {data.role_categories.map((r) => ROLE_LABEL[r] || r).join(' · ')}</>
                )}
              </div>
            </div>
            <Button
              variant="ghost"
              className="text-cyan-300 border border-cyan-300/30 hover:bg-cyan-300/10"
              onClick={regenerate}
              disabled={regenLoading}
              data-testid="regenerate-ai-btn"
            >
              {regenLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
              Refresh AI Insights
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6">
        {/* Iter-258 — Sentiment Hero block (purple gradient). Always
            renders so the dashboard never feels empty; live data fills
            in once talents submit recaps. */}
        <div
          className="rounded-2xl mt-5 mb-5 p-5 sm:p-6 text-white shadow-lg"
          style={{ background: 'linear-gradient(135deg, #6d28d9 0%, #7c3aed 50%, #a855f7 100%)' }}
          data-testid="sentiment-hero"
        >
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="min-w-0">
              <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-purple-100/90">
                Live event sentiment
              </div>
              {exec.sentiment_score != null ? (
                <>
                  <div className="text-4xl sm:text-5xl font-extrabold mt-2 leading-none">
                    {Math.round(exec.sentiment_score)}<span className="text-2xl text-purple-100">/100</span>
                  </div>
                  <div className="mt-2 text-sm text-purple-50/90">
                    {exec.sentiment_score >= 80
                      ? `${Math.round(exec.sentiment_score)}% Positive · brand sentiment is exceeding expectations.`
                      : exec.sentiment_score >= 60
                        ? `${Math.round(exec.sentiment_score)}% Positive · sentiment is healthy.`
                        : `${Math.round(exec.sentiment_score)}% Positive · review talent recaps to address mixed signal.`}
                  </div>
                </>
              ) : (
                <>
                  <div className="text-3xl sm:text-4xl font-extrabold mt-2 leading-none text-purple-100/95">
                    Awaiting recaps
                  </div>
                  <div className="mt-2 text-sm text-purple-50/85">
                    Live sentiment score appears here once talents submit their event recaps.
                  </div>
                </>
              )}
            </div>
            <div className="bg-white/10 rounded-xl px-4 py-3 backdrop-blur-sm">
              <div className="text-[10px] uppercase tracking-wider text-purple-100/80">Performance</div>
              <div className="text-2xl font-bold mt-1">{exec.performance_score ?? 0}/100</div>
            </div>
          </div>
        </div>

        {/* Iter-257 — Executive KPI tiles are deep-linkable. Clicking
            scrolls to the section that explains the metric. */}
        <SectionHeader icon={Award} title="Executive Summary"
          subtitle="At-a-glance performance for this activation · tap a tile to jump to the detail" />
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
          <KPI label="Activations" value={fmt(exec.events)} testid="exec-events"
               onClick={() => document.getElementById('campaign-performance')?.scrollIntoView({ behavior: 'smooth', block: 'start' })} />
          <KPI label="Staff" value={fmt(exec.staff)} testid="exec-staff"
               onClick={() => document.getElementById('team-performance')?.scrollIntoView({ behavior: 'smooth', block: 'start' })} />
          <KPI label="Engagements" value={fmt(exec.engagements)} testid="exec-engagements"
               onClick={() => document.getElementById('campaign-performance')?.scrollIntoView({ behavior: 'smooth', block: 'start' })} />
          <KPI label="Leads" value={fmt(exec.leads)} testid="exec-leads"
               onClick={() => document.getElementById('campaign-performance')?.scrollIntoView({ behavior: 'smooth', block: 'start' })} />
          <KPI label="Samples" value={fmt(exec.samples)} testid="exec-samples"
               onClick={() => document.getElementById('campaign-performance')?.scrollIntoView({ behavior: 'smooth', block: 'start' })} />
          <KPI label="Sentiment" value={exec.sentiment_score != null ? `${exec.sentiment_score}/100` : '—'}
            testid="exec-sentiment"
            onClick={() => document.getElementById('ai-insights')?.scrollIntoView({ behavior: 'smooth', block: 'start' })} />
          <KPI label="Performance" value={`${exec.performance_score}/100`}
            sub="Composite score" testid="exec-perf" />
        </div>

        {/* Campaign Performance tiles */}
        <div id="campaign-performance" />
        <SectionHeader icon={TrendingUp} title="Campaign Performance" />
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3">
          <KPI label="Samples Distributed" value={fmt(perf.samples_distributed)} testid="perf-samples" />
          <KPI label="Consumer Engagements" value={fmt(perf.consumer_engagements)} testid="perf-engagements" />
          <KPI label="Product Demos" value={fmt(perf.product_demos)} testid="perf-demos" />
          <KPI label="Leads Collected" value={fmt(perf.leads_collected)} testid="perf-leads" />
          <KPI label="QR Scans" value={fmt(perf.qr_scans)} testid="perf-qr" />
          <KPI label="Email Signups" value={fmt(perf.email_signups)} testid="perf-email" />
          <KPI label="Coupon Redemptions" value={fmt(perf.coupon_redemptions)} testid="perf-coupons" />
          <KPI label="Retail Sales" value={money(perf.retail_sales_cents)} testid="perf-sales" />
          <KPI label="Booth Traffic" value={fmt(perf.booth_traffic)} testid="perf-booth" />
          {(perf.content_pieces_delivered > 0 || perf.posts_created > 0) && (
            <KPI label="Content Pieces" value={fmt(perf.content_pieces_delivered)} testid="perf-content" />
          )}
        </div>

        {/* Iter-258 — Conversion Funnel.
            Visualises Engagements → Demos → Leads → Sales drop-off so
            clients can spot weak conversion stages at a glance. */}
        <SectionHeader icon={TrendingUp} title="Conversion Funnel"
          subtitle="Where prospects move (or drop) along the activation flow" />
        <ConversionFunnel perf={perf} />

        {data.daily_trend?.length > 1 && (
          <>
            <SectionHeader title="Daily Trend" />
            <div className="bg-white border border-slate-200 rounded-xl p-4">
              <ResponsiveContainer width="100%" height={220}>
                <LineChart data={data.daily_trend}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Line type="monotone" dataKey="engagements" stroke="#0DEAE3" strokeWidth={2} />
                  <Line type="monotone" dataKey="samples" stroke="#6366F1" strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </>
        )}

        {/* AI Insights */}
        <div id="ai-insights" />
        <SectionHeader icon={Sparkles} title="AI Insights"
          subtitle="Powered by Claude Sonnet 4.5 — grounded in this event's voice recaps" />
        {!ai?.summary && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-900">
            No AI rollup yet. Tap Refresh AI Insights after talents submit their recaps.
          </div>
        )}
        {ai?.summary && (
          <div className="bg-white border border-slate-200 rounded-xl p-5">
            <p className="text-slate-800 leading-relaxed italic" data-testid="ai-summary">
              {ai.summary}
            </p>
            <div className="flex flex-wrap gap-2 mt-3">
              <Badge className="bg-cyan-100 text-cyan-900">
                Sentiment: {ai.sentiment_label || '—'}
                {ai.sentiment_score ? ` (${ai.sentiment_score}/100)` : ''}
              </Badge>
              <Badge variant="outline">
                {ai.recap_count} recaps · {ai.model}
              </Badge>
            </div>
          </div>
        )}

        {/* AI Signals */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-4">
          <SignalCard
            title="Most Common Questions"
            icon={MessageCircle}
            items={data.ai_signals?.faqs || []}
            empty="No FAQs captured yet."
            testid="signal-faqs"
          />
          <SignalCard
            title="Top Objections"
            icon={AlertTriangle}
            items={data.ai_signals?.objections || []}
            empty="No objections captured."
            testid="signal-objections"
          />
          <SignalCard
            title="Competitor Mentions"
            icon={Sparkles}
            items={data.ai_signals?.competitor_signals || []}
            empty="No competitor mentions."
            testid="signal-competitors"
          />
        </div>

        {/* AI Recommendations — always rendered so testids stay stable */}
        <SectionHeader icon={Sparkles} title="AI Recommendations" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <RecCard label="Staffing" items={ai?.recommendations?.staffing} testid="rec-staffing" />
          <RecCard label="Scheduling" items={ai?.recommendations?.scheduling} testid="rec-scheduling" />
          <RecCard label="Product Messaging" items={ai?.recommendations?.messaging} testid="rec-messaging" />
        </div>
        {ai?.opportunities?.length > 0 && (
          <>
            <SectionHeader title="AI Opportunities" subtitle="Strategic actions to consider next" />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {ai.opportunities.map((op, i) => (
                <div key={i} className="bg-white border border-slate-200 rounded-xl p-4"
                  data-testid={`opportunity-${i}`}>
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <div className="font-semibold text-slate-900">{op.title}</div>
                    <Badge className={
                      op.impact === 'high'
                        ? 'bg-rose-100 text-rose-900'
                        : op.impact === 'low'
                          ? 'bg-slate-100 text-slate-700'
                          : 'bg-amber-100 text-amber-900'
                    }>{(op.impact || 'medium').toUpperCase()}</Badge>
                  </div>
                  <p className="text-sm text-slate-600">{op.detail}</p>
                </div>
              ))}
            </div>
          </>
        )}

        {/* Team Performance */}
        <div id="team-performance" />
        <SectionHeader icon={Users} title="Team Performance" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <KPI label="Staff Count" value={fmt(team.staff_count)} testid="team-staff" />
          <KPI label="Completion Rate"
            value={team.completion_rate != null ? `${team.completion_rate}%` : '—'}
            testid="team-completion" />
          <KPI label="Recap Completion"
            value={team.recap_completion_rate != null ? `${team.recap_completion_rate}%` : '—'}
            testid="team-recap" />
          <KPI label="Photos Submitted" value={fmt(team.photos_submitted)} testid="team-photos" />
        </div>
        {team.top_performers?.length > 0 && (
          <div className="bg-white border border-slate-200 rounded-xl p-4 mt-4">
            <div className="text-sm font-semibold text-slate-700 mb-2">Top Performers</div>
            <ul className="space-y-2">
              {team.top_performers.map((tp, i) => (
                <li key={i} className="flex items-center justify-between border-b border-slate-100 pb-2 last:border-0"
                  data-testid={`top-performer-${i}`}>
                  <div>
                    <div className="font-medium text-slate-900">{tp.name}</div>
                    <div className="text-xs text-slate-500">
                      {fmt(tp.engagements)} engagements · {fmt(tp.samples)} samples · {fmt(tp.leads)} leads
                    </div>
                  </div>
                  <div className="text-right">
                    {tp.rating > 0 && <div className="text-amber-500 font-semibold">★ {tp.rating.toFixed(1)}</div>}
                    {tp.recap_done && <Badge variant="outline" className="text-emerald-600 border-emerald-300">Recap ✓</Badge>}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Benchmarks */}
        <SectionHeader title="Benchmarks"
          subtitle="vs prior quarter · vs year ago · vs network · vs similar campaigns" />
        <BenchmarkTable bench={bench} testid="benchmark-table" />

        {/* Iter-237 — Creator Attribution */}
        <CreatorAttributionPanel
          attribution={data.attribution}
          objective={data.attribution?.revenue_by_objective?.[0]?.objective}
        />

        {/* Photos */}
        {data.photos?.length > 0 && (
          <>
            <SectionHeader icon={Camera} title="Campaign Photos"
              subtitle="Auto-selected from approved submissions" />
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3"
              data-testid="event-photos">
              {data.photos.map((p, i) => (
                <a key={i} href={p.url} target="_blank" rel="noreferrer"
                  className="block aspect-video bg-slate-200 rounded-lg overflow-hidden border border-slate-200">
                  <img src={p.url} alt={p.category || 'Event photo'}
                    className="w-full h-full object-cover" />
                </a>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function SignalCard({ title, icon: Icon, items, empty, testid }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid={testid}>
      <div className="flex items-center gap-2 mb-2">
        {Icon && <Icon className="w-4 h-4 text-cyan-500" />}
        <div className="text-sm font-semibold text-slate-900">{title}</div>
      </div>
      {items.length === 0 && <p className="text-xs text-slate-500">{empty}</p>}
      <ul className="space-y-1.5">
        {items.slice(0, 5).map((it, i) => (
          <li key={i} className="text-sm text-slate-700 flex items-start justify-between gap-2">
            <span className="leading-snug">{it.text}</span>
            <span className="text-xs text-slate-400 shrink-0">×{it.count}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function RecCard({ label, items, testid }) {
  return (
    <div className="bg-gradient-to-br from-slate-900 to-slate-800 text-white rounded-xl p-4"
      data-testid={testid}>
      <div className="text-xs uppercase tracking-wider text-cyan-300 mb-2">{label}</div>
      {(!items || items.length === 0) && (
        <p className="text-sm text-slate-300">No recommendations.</p>
      )}
      <ul className="space-y-2">
        {(items || []).slice(0, 5).map((it, i) => (
          <li key={i} className="text-sm leading-snug">
            <span className="text-cyan-300 mr-1">►</span>{it}
          </li>
        ))}
      </ul>
    </div>
  );
}

function BenchmarkTable({ bench, testid }) {
  const rows = [
    { key: 'consumer_engagements', label: 'Engagements', isMoney: false },
    { key: 'samples_distributed',  label: 'Samples',     isMoney: false },
    { key: 'leads_collected',      label: 'Leads',       isMoney: false },
    { key: 'qr_scans',             label: 'QR Scans',    isMoney: false },
    { key: 'retail_sales_cents',   label: 'Retail Sales', isMoney: true },
  ];
  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid={testid}>
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider">
          <tr>
            <th className="text-left px-4 py-2.5">Metric</th>
            <th className="text-right px-3 py-2.5">Current</th>
            <th className="text-right px-3 py-2.5">vs Prior Q</th>
            <th className="text-right px-3 py-2.5">vs YoY</th>
            <th className="text-right px-3 py-2.5">vs Network</th>
            <th className="text-right px-3 py-2.5">vs Similar</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const item = bench[r.key] || {};
            const cur = r.isMoney
              ? `$${((item.current || 0) / 100).toLocaleString(undefined, { maximumFractionDigits: 0 })}`
              : (item.current || 0).toLocaleString();
            return (
              <tr key={r.key} className="border-t border-slate-100" data-testid={`bench-${r.key}`}>
                <td className="px-4 py-2 text-slate-900 font-medium">{r.label}</td>
                <td className="px-3 py-2 text-right text-slate-900">{cur}</td>
                <td className="px-3 py-2 text-right"><Delta pct={item.vs_prior_quarter_pct} label={`${r.key}-pq`} /></td>
                <td className="px-3 py-2 text-right"><Delta pct={item.vs_year_ago_pct} label={`${r.key}-yoy`} /></td>
                <td className="px-3 py-2 text-right"><Delta pct={item.vs_network_pct} label={`${r.key}-net`} /></td>
                <td className="px-3 py-2 text-right"><Delta pct={item.vs_similar_pct} label={`${r.key}-sim`} /></td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <div className="px-4 py-2 text-xs text-slate-500 border-t border-slate-100 bg-slate-50">
        Network benchmark: {bench.network_clients || 0} comparable client(s).
      </div>
    </div>
  );
}


/**
 * ConversionFunnel — Iter-258
 *
 * Polished funnel visualisation showing how the activation moved
 * prospects from raw engagement → product demo → lead → retail sale.
 * Uses live `perf` values from the existing campaign_performance payload;
 * no placeholder data.
 */
function ConversionFunnel({ perf }) {
  const stages = [
    { key: 'engagements', label: 'Consumer Engagements', value: perf.consumer_engagements || 0, accent: 'from-cyan-500 to-cyan-400' },
    { key: 'demos',       label: 'Product Demos',         value: perf.product_demos || 0,         accent: 'from-violet-500 to-violet-400' },
    { key: 'leads',       label: 'Leads Collected',       value: perf.leads_collected || 0,       accent: 'from-fuchsia-500 to-fuchsia-400' },
    { key: 'sales',       label: 'Retail Sales',          value: Math.round((perf.retail_sales_cents || 0) / 100), accent: 'from-emerald-500 to-emerald-400', isMoney: true },
  ];
  const max = Math.max(...stages.map((s) => s.value), 1);

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 sm:p-5" data-testid="conversion-funnel">
      <div className="space-y-3">
        {stages.map((s, i) => {
          const pct = Math.max(6, Math.round((s.value / max) * 100));
          const prev = i > 0 ? stages[i - 1].value : null;
          const dropRate = prev && prev > 0 ? Math.round((s.value / prev) * 100) : null;
          return (
            <div key={s.key} data-testid={`funnel-stage-${s.key}`}>
              <div className="flex items-center justify-between text-xs text-slate-600 mb-1">
                <span className="font-semibold uppercase tracking-wider">{s.label}</span>
                <span className="text-slate-500">
                  {dropRate != null && (
                    <span className={`mr-2 font-semibold ${dropRate >= 50 ? 'text-emerald-600' : dropRate >= 20 ? 'text-amber-600' : 'text-rose-600'}`}>
                      {dropRate}% conv.
                    </span>
                  )}
                  <span className="text-slate-900 font-bold">
                    {s.isMoney ? `$${s.value.toLocaleString()}` : s.value.toLocaleString()}
                  </span>
                </span>
              </div>
              <div className="h-8 bg-slate-100 rounded-md overflow-hidden">
                <div
                  className={`h-full bg-gradient-to-r ${s.accent} rounded-md transition-all duration-500`}
                  style={{ width: `${pct}%` }}
                />
              </div>
            </div>
          );
        })}
      </div>
      <div className="text-[11px] text-slate-500 mt-3">
        Conversion rate compares each stage to the one above it. Empty stages signal where the activation is leaking value.
      </div>
    </div>
  );
}
